import { ScrollArea, Stack, useMantineTheme } from '@mantine/core';
import { useInputState } from '@mantine/hooks';
import type { TeamOut } from '@repo/api-codegen';
import { useApiListTeamsV2 } from '@repo/api-codegen/v2';
import { fuzzySearchFilter } from '@repo/common/utils/fuse';
import { isNil } from 'lodash-es';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMatch, useNavigate } from 'react-router';
import type { IBaseModel } from '../../../../api';
import { useAuthUser, useQuestionsEnabled } from '../../../../api';
import PaletteInput from '../PaletteInput';
import CommandPaletteResults from './components/CommandPaletteResults';
import type { ICommandListItem } from './constants';
import { staticCommandList } from './constants';
import {
	generateCreateBreadcrumbs,
	generateCreateCommandList,
	generateTeamBreadcrumbs,
	generateTeamCommandsList,
	getInitialResults,
	getSearchResults,
	handleCommandPaletteItem,
} from './utils';

interface CommandPaletteProps {
	actions?: ICommandListItem<IBaseModel>[];
}

function CommandPalette({ actions }: CommandPaletteProps) {
	const navigate = useNavigate();
	const [searchTerm, setSearchTerm] = useInputState('');
	const viewport = useRef<HTMLInputElement>(null);
	const [selectedItem, setSelectedItem] = useState<number>(0);
	const [containsBreadcrumbs, setContainsBreadcrumbs] = useState(false);
	const { workspace, user } = useAuthUser();
	const questionsEnabled = useQuestionsEnabled();

	const urlTeam = useMatch('/teams/:teamId/*');
	const currentTeamId = urlTeam?.params.teamId ?? null;

	const { data: teams } = useApiListTeamsV2({
		queryParams: {
			only_joined: true,
		},
	});

	const actionsCommandList = useMemo(() => actions ?? [], [actions]);

	const teamsCommandList = useMemo(
		() =>
			teams?.map((team: TeamOut) => generateTeamCommandsList(team))?.flat() ??
			[],
		[teams]
	);

	const [result, setResult] = useState(
		getInitialResults(
			workspace,
			teamsCommandList,
			actionsCommandList,
			questionsEnabled
		)
	);

	const displayedResults = fuzzySearchFilter(searchTerm, result, [
		'name',
		'hotkey',
	]);

	const hotkeyList = useMemo(
		() => [
			...staticCommandList,
			...generateCreateCommandList(workspace, questionsEnabled).filter(
				(item) => item.show
			),
		],
		[workspace, questionsEnabled]
	);

	useEffect(() => {
		// TODO: Make readable constants for these magic number used to scroll items
		// in CommandPalette.
		if (viewport.current !== null) {
			const top =
				selectedItem * 36 - 324 === -360 ? 288 : selectedItem * 36 - 324;
			viewport.current.scrollTo({
				top,
			});
		}
	}, [selectedItem]);

	const handleChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			setSelectedItem(0);
			const { value } = event.target;
			const commandListItem = hotkeyList.find(
				(item) => item.hotkey?.toLowerCase() === value.toLowerCase()
			);
			if (commandListItem) {
				handleCommandPaletteItem(
					commandListItem,
					navigate,
					user,
					currentTeamId ?? commandListItem.team
				);
			}
			if (value === '') {
				setContainsBreadcrumbs(false);
				setResult(
					getInitialResults(
						workspace,
						teamsCommandList,
						actionsCommandList,
						questionsEnabled
					)
				);
			} else {
				setResult(
					getSearchResults(
						workspace,
						teamsCommandList,
						actionsCommandList,
						questionsEnabled
					)
				);
			}
			setSearchTerm(value);
		},
		[
			actionsCommandList,
			currentTeamId,
			hotkeyList,
			navigate,
			setSearchTerm,
			teamsCommandList,
			user,
			workspace,
			questionsEnabled,
		]
	);

	const handleSearchInputChange = useCallback(
		(event: ICommandListItem<IBaseModel>) => {
			if (event.type === 'OPEN' && !containsBreadcrumbs) {
				setSearchTerm(`${event.name} / `);
				setResult(generateTeamBreadcrumbs(event, workspace, questionsEnabled));
				setContainsBreadcrumbs(true);
				return;
			}
			if (event.type !== 'static' && !containsBreadcrumbs) {
				setSearchTerm(`${event.name} / `);
				setResult(generateCreateBreadcrumbs(event, teams ?? []));
				setContainsBreadcrumbs(true);
			}
		},
		[containsBreadcrumbs, setSearchTerm, teams, workspace, questionsEnabled]
	);

	const theme = useMantineTheme();

	return (
		<Stack spacing={0} data-testid="command-palette">
			<PaletteInput
				type="command"
				value={searchTerm}
				placeholder="Search actions"
				onChange={handleChange}
			/>
			<ScrollArea mt={theme.spacing.xs} h={400} viewportRef={viewport}>
				<CommandPaletteResults
					searchTerm={searchTerm}
					searchResults={
						displayedResults.filter(
							(item) => !isNil(item)
						) as ICommandListItem<IBaseModel>[]
					}
					onSearchInputChange={handleSearchInputChange}
					selectedItem={selectedItem}
					setSelectedItem={setSelectedItem}
				/>
			</ScrollArea>
		</Stack>
	);
}

export default CommandPalette;
